/**
 * Cross-process audio-debug contract: schemas, IPC event names, and the
 * master-flag helpers. Process-specific code lives elsewhere — this module
 * must stay callable from both main and renderers.
 *
 * Data flow (no GlobalState except the master flag):
 *
 *   producers ── AUDIO_DEBUG_REPORT_EVENT (targeted invoke) ──▶ main store
 *   main store ── AUDIO_DEBUG_TOPIC (broadcast) ──▶ subscribed consumers
 *   consumer on open ── derived snapshot event ──▶ full snapshot
 *
 * Renderer producers send reports with the helpers in
 * `@renderer/services/audio-transcription/audio-debug` (which also owns
 * the enumerateDevices snapshots and markdown formatting). Main-side
 * producers (Recall runtime, WAV dump) skip the invoke hop and call
 * `applyAudioDebugReport` on `@main/utils/audio-debug-store` directly.
 * With the overlay closed there are zero broadcast consumers, so nothing
 * fans out to renderers.
 *
 * Master switch: `AUDIO_DEBUG_ENABLED_KEY` (GlobalState, low-frequency).
 * The overlay renders nothing when off, but producers keep the main store
 * warm so opening the overlay mid-session shows current routing, channel
 * cadence, and recent events immediately. Toggle from any devtools console
 * with `GlobalState.set('audio-debug.enabled', true)`, or with the
 * overlay's Cmd/Ctrl+Shift+D hotkey.
 */
import { GlobalState } from '@highlight/ipc'
import type { MeetingDetectionDebugState } from '@shared/types/meeting-detection-debug'

/** Master UI flag for the developer overlay. Default false. */
export const AUDIO_DEBUG_ENABLED_KEY = 'audio-debug.enabled'

/** Broadcast topic carrying the full {@link AudioDebugSnapshot} on every
 *  store mutation. Delivered only to registered consumers. */
export const AUDIO_DEBUG_TOPIC = 'audio-debug.state'

/** Producer renderer → main: apply one {@link AudioDebugReport} to the store. */
export const AUDIO_DEBUG_REPORT_EVENT = 'audio-debug:report'

/** Channel labels used by the electron audio runtime's capture pipeline.
 *  Mirrors the renderer's `ChannelLabel` (structurally identical) without
 *  making shared code depend on renderer types. */
export type AudioDebugChannelLabel = 'mic' | 'sys'

/** Cap on the events list to keep IPC payloads small (each event is
 *  ~80-150 bytes serialised). 50 events ≈ 1 minute of activity at
 *  the typical event cadence; older history is in the regular
 *  console log. */
export const AUDIO_DEBUG_EVENT_LOG_MAX = 50

// ── Schemas ─────────────────────────────────────────────────────

/** Raw routing snapshot — same information as the compact routing log
 *  string but split into structured fields so the overlay can highlight
 *  the HFP risk badge etc. without re-parsing. */
export interface AudioDebugRouting {
  /** Wall-clock ms (Date.now()) when the snapshot was taken. */
  takenAtMs: number
  /** Human-readable label of the active default audiooutput device. */
  defaultOutputLabel: string
  /** Web-spec groupId — devices with the same groupId are the same
   *  physical hardware. Used by the overlay to highlight the same
   *  device across the input/output lists. */
  defaultOutputGroupId: string
  defaultInputLabel: string
  defaultInputGroupId: string
  /** Subset of audiooutput entries whose label matches a Bluetooth
   *  pattern. Useful when the user has multiple BT outputs paired
   *  but only one is the current default. */
  bluetoothOutputs: Array<{ label: string; groupId: string }>
  bluetoothInputs: Array<{ label: string; groupId: string }>
  /** True iff the default input shares groupId with the default
   *  audiooutput device — i.e. the same physical device is
   *  serving as both mic and the system's current output sink,
   *  which on Bluetooth headsets forces an A2DP→HFP renegotiation
   *  when the mic opens AND degrades the audio CATAP is capturing
   *  off the default output. Comparing only against the *default*
   *  output (not "any output") matters: if AirPods are the default
   *  input but the default output is the built-in speakers, the
   *  AirPods will still flip to HFP when the mic opens, but CATAP
   *  is tapping the built-in speakers — so loopback quality is
   *  unaffected and there is no reason to override the user's mic
   *  choice in `pickNonBluetoothMicForHfpAvoidance`. */
  hfpTriggerRisk: boolean
}

/** Per-channel state snapshot, refreshed periodically. */
export interface AudioDebugChannelState {
  /** Wall-clock ms when this snapshot was taken. */
  takenAtMs: number
  label: AudioDebugChannelLabel
  /** Lifecycle phase. `acquired` means the capture is alive and
   *  delivering frames; `ended` means the underlying track ended
   *  and recovery has not yet re-acquired. */
  phase: 'idle' | 'acquiring' | 'acquired' | 'ended'
  /** Sample rate the OS reported on the raw track (`48000` for a
   *  modern CATAP loopback claim). 0 if not yet known. */
  claimedSampleRate: number
  /** Reserved for a future passive rate measurement. Currently always
   *  0; keeping the field avoids a state-key change when it is added. */
  measuredSampleRate: number
  /** TARGET_SAMPLE_RATE the pipeline is producing. */
  targetSampleRate: number
  /** Frames delivered to the channel since the last snapshot. */
  framesPerSecond: number
  /** Bytes sent over the WS in the last snapshot window, expressed
   *  as KB/s so the overlay UI can display it without conversion.
   *  Critical signal: if this stays at 0 while `framesPerSecond` is
   *  >0, our transport is wedged; if it tracks `framesPerSecond * 32`
   *  (16kHz × 2 bytes ÷ 1000) but `transcriptsTotal` doesn't grow,
   *  Deepgram is receiving audio but returning nothing — pinpoints
   *  the actual failure layer. */
  bytesSentKbps: number
  /** Cumulative bytes sent over the WS since channel creation. */
  bytesSentTotal: number
  /** Bytes currently buffered (pre-WS or during reconnect). When this
   *  grows unboundedly, the WS is stuck and we're about to drop
   *  audio on the floor. */
  bufferedBytes: number
  /** Cumulative transcript messages received from Deepgram since
   *  channel creation. The single most useful "is this actually
   *  working?" indicator: zero transcripts after seconds of audio
   *  flowing means Deepgram thinks the audio is silent or invalid.
   *  Combined with `bytesSentTotal`, lets us prove "we sent X bytes,
   *  got Y transcripts back" at a glance. */
  transcriptsTotal: number
  /** Inbound WS messages of any type (transcripts + control). When
   *  this is 0 but `bytesSentTotal` is climbing, the WS is unhealthy
   *  upstream. */
  wsMessagesTotal: number
  /** RMS of the last published window (0–1). */
  rmsMean: number
  /** Peak absolute amplitude of the last window (0–1). */
  peakAbs: number
  /** Cumulative frames since the channel was created. */
  totalFrames: number
  /** WebSocket state for this channel. Mirrors the existing diag
   *  field. */
  wsReadyState: 'CONNECTING' | 'OPEN' | 'CLOSING' | 'CLOSED' | 'NULL'
  serverReady: boolean
  /** Track liveness (`getTracks()[0].readyState === 'live'`). */
  liveTrack: boolean
  /** Most recent audio-pipeline event for this channel, for the
   *  hero line of the channel card. The full event log is in the
   *  snapshot's `events` list. */
  lastEvent: string | null
}

// ── WAV dump (overlay → main → audio-runtime renderer) ─────────────
//
// Lets the debug overlay save the per-channel debug ring buffers (the
// exact PCM bytes shipped to Deepgram) as .wav files on disk and play
// them back inline, so pipeline quality can be verified by ear instead
// of trusted blindly. The overlay can only invoke into main, and only
// main can invoke into the hidden audio-runtime window, hence the two
// hops. The WAV bytes cross IPC once (runtime → main, to be written to
// disk); playback in the overlay streams the files back through the
// `audio-debug://` protocol instead of a second byte-carrying hop.

/** Overlay → main: dump all buffered channel audio to disk. Returns
 *  {@link AudioDebugDumpResult}. */
export const AUDIO_DEBUG_DUMP_WAVS_EVENT = 'audio-debug:dump-wavs'

/** Overlay → main: reveal a previously dumped file in Finder/Explorer.
 *  Payload: {@link AudioDebugRevealPayload}. */
export const AUDIO_DEBUG_REVEAL_EVENT = 'audio-debug:reveal-dump'

/** Main → audio-runtime renderer: collect WAV bytes for every populated
 *  debug-buffer stage on every active channel. Returns
 *  {@link AudioRuntimeWavDumpResponse}. */
export const AUDIO_RUNTIME_DUMP_WAVS_EVENT = 'audio-runtime:dumpWavs'

/** One tapped pipeline stage, as returned by the audio-runtime renderer.
 *  Field names match the renderer's `DebugCaptureStage` so the runtime
 *  can forward a stage as `{ ...stage, channel }`. */
export interface AudioDebugWavStage {
  channel: AudioDebugChannelLabel
  /** Stage identifier: `'final'` = byte-identical to the wire,
   *  `'raw'` = pre-resample mono downmix at the device's native rate. */
  name: string
  description: string
  sampleRate: number
  durationSec: number
  /** Complete RIFF/WAVE file bytes. */
  wav: Uint8Array
}

export interface AudioRuntimeWavDumpResponse {
  stages: Array<AudioDebugWavStage>
  /** False when no channel is running at all — lets main distinguish
   *  "nothing recording" from "recording but nothing buffered yet". */
  anyChannelActive: boolean
}

/** A dumped stage after main wrote it to disk. Metadata only — the
 *  overlay plays the file back through `url`, so the WAV bytes never
 *  make a second IPC hop. */
export interface AudioDebugDumpedFile extends Omit<AudioDebugWavStage, 'wav'> {
  /** Absolute path on disk, for "reveal in Finder". */
  filePath: string
  /** `audio-debug://` URL served by main for inline playback. */
  url: string
}

export type AudioDebugDumpResult =
  | { ok: true; dirPath: string; files: Array<AudioDebugDumpedFile> }
  /** `reason` is human-readable: runtime not active, no audio buffered
   *  yet, write failure, … */
  | { ok: false; reason: string }

export interface AudioDebugRevealPayload {
  filePath: string
}

/** A single audio-pipeline event for the rolling log. */
export interface AudioDebugEvent {
  /** Wall-clock ms (Date.now()) of the event. */
  atMs: number
  /** Channel the event applies to. `'recall'` for Recall-runtime
   *  events, `null` for service-wide events (start, stop, etc.). */
  channel: AudioDebugChannelLabel | 'recall' | null
  /** Severity for UI styling. `warn` is a yellow row, `error` red,
   *  `info` neutral. */
  severity: 'info' | 'warn' | 'error'
  /** Free-form short message. */
  message: string
}

/**
 * Recall-runtime state snapshot for the overlay's Recall card.
 *
 * Capture, WebSockets, and upload live inside the Recall SDK, so the
 * electron runtime's byte-level channel stats have no equivalent here.
 * What IS observable — and what this reports — is the realtime-event
 * surface: mixed-audio cadence, transcript-event counts, and lifecycle.
 */
export interface AudioDebugRecallState {
  /** Wall-clock ms when this snapshot was taken. */
  takenAtMs: number
  sdkState: 'recording' | 'idle' | 'paused'
  /** Recall SDK window id of the active (or last) recording. */
  windowId: string | null
  recallRecordingId: string | null
  audioSessionId: string | null
  /** `audio_mixed_raw.data` events/s over the last snapshot window.
   *  The Recall analog of `framesPerSecond`: 0 while recording means
   *  the SDK is delivering no audio. */
  audioEventsPerSecond: number
  /** Approximate KB/s of mixed PCM received from the SDK (decoded
   *  base64 size) over the last snapshot window. */
  audioKbps: number
  /** Last computed activity level (0–5 meter scale, not raw RMS). */
  lastAudioLevel: number
  lastAudioAtMs: number
  /** Cumulative realtime transcript events since recording start. */
  transcriptEventsTotal: number
  partialEventsTotal: number
  providerEventsTotal: number
  lastTranscriptAtMs: number
  /** Last `network-status` reported by the SDK. Null until one fires. */
  networkStatus: 'connected' | 'disconnected' | 'reconnected' | null
}

// ── Store contract ──────────────────────────────────────────────

/** Everything the overlay renders, owned by the main-process store
 *  (`@main/utils/audio-debug-store`). Small enough (~few KB) that the
 *  store broadcasts the whole thing on every mutation — no delta
 *  machinery needed at this cadence. */
export interface AudioDebugSnapshot {
  /** Incremented by the main snapshot broadcaster on every mutation. */
  revision: number
  routing: AudioDebugRouting | null
  mic: AudioDebugChannelState | null
  sys: AudioDebugChannelState | null
  recall: AudioDebugRecallState | null
  meetingDetection: MeetingDetectionDebugState | null
  events: Array<AudioDebugEvent>
}

export const EMPTY_AUDIO_DEBUG_SNAPSHOT: AudioDebugSnapshot = {
  revision: 0,
  routing: null,
  mic: null,
  sys: null,
  recall: null,
  meetingDetection: null,
  events: [],
}

/** One mutation of the main store. Renderer producers send these over
 *  {@link AUDIO_DEBUG_REPORT_EVENT}; main producers apply them directly. */
export type AudioDebugReport =
  /** Latest routing snapshot. Last-write-wins because mic and system
   *  capture observe the same physical device routing. */
  | { kind: 'routing'; routing: AudioDebugRouting }
  /** Per-channel state snapshot from the diagnostics tick. */
  | { kind: 'channel'; state: AudioDebugChannelState }
  /** Channel teardown — clears the slot so the overlay doesn't show a
   *  stale card. */
  | { kind: 'channel-clear'; label: AudioDebugChannelLabel }
  /** Recall-runtime snapshot (5s tick + lifecycle transitions). */
  | { kind: 'recall'; state: AudioDebugRecallState }
  /** Meeting lifecycle state merged from its independent main-process producers. */
  | {
      kind: 'meeting-detection'
      source: string
      section: Partial<Pick<MeetingDetectionDebugState, 'audioNote' | 'detector' | 'recall'>>
    }
  /** Append to the rolling event log (store applies the cap). */
  | { kind: 'event'; event: AudioDebugEvent }

// ── Master flag helpers ─────────────────────────────────────────

/** Synchronous check for work that should only run when the overlay is open. */
export function isAudioDebugEnabled(): boolean {
  return GlobalState.get<boolean>(AUDIO_DEBUG_ENABLED_KEY, false) === true
}

/**
 * Subscribe to master flag transitions. The audio runtime calls this
 * once at startup with a snapshot fn; whenever the user enables the
 * overlay (Cmd+Shift+D), the snapshot fn fires so the overlay's
 * routing card is populated immediately instead of waiting for the
 * next acquire. If the flag is already on at subscribe time (overlay
 * enabled before the caller existed), `onEnabled` fires immediately —
 * no transition would ever arrive for that state. On disable, the
 * optional `onDisabled` fires so callers can release debug-only
 * resources (e.g. the per-channel WAV ring buffers); the main store
 * wipes its own state on the same transition.
 *
 * Both callbacks are fire-and-forget — errors are caught and logged
 * here so a flaky enumerateDevices() can't take down the audio
 * runtime.
 *
 * Returns an unsubscribe function for clean teardown.
 */
export function watchAudioDebugEnabled(
  onEnabled: () => void | Promise<void>,
  onDisabled?: () => void | Promise<void>,
): () => void {
  // Fire-and-forget: never let an overlay-side concern block
  // anything in the audio pipeline.
  const fireEnabled = (): void => {
    Promise.resolve(onEnabled()).catch((err) => {
      console.warn('[audio-debug] enable handler threw:', err)
    })
  }

  let last = isAudioDebugEnabled()
  const handler = (next: boolean): void => {
    const enabled = next === true
    if (enabled === last) return
    last = enabled
    if (!enabled) {
      if (onDisabled) {
        Promise.resolve(onDisabled()).catch((err) => {
          console.warn('[audio-debug] disable handler threw:', err)
        })
      }
      return
    }
    fireEnabled()
  }
  GlobalState.on(AUDIO_DEBUG_ENABLED_KEY, handler)
  if (last) {
    fireEnabled()
  }
  return () => GlobalState.off(AUDIO_DEBUG_ENABLED_KEY, handler)
}
